import { localeFormats, t } from '../i18n';
import { citation, isolerLtr } from '../i18n/sens';
import type { Offre, Raison, SourceOffre } from '../types/offres';

/** `max` raisons au plus sur la carte (trois, deux sur petit écran) ; une alerte reste toujours visible. */
export function raisonsAffichees(raisons: Raison[], max = 3): Raison[] {
  const oks = raisons.filter((r) => r.ok);
  const alertes = raisons.filter((r) => !r.ok);
  if (alertes.length === 0) return oks.slice(0, max);
  return [...oks.slice(0, max - 1), alertes[0], ...oks.slice(max - 1), ...alertes.slice(1)].slice(0, max);
}

function valeurAffichee(r: Raison): string | number | undefined {
  if (r.valeur === undefined) return undefined;
  if (r.code === 'contrat' || r.code === 'contrat_different') return t(`offres.contratsEnLigne.${r.valeur}`);
  if (r.code === 'rythme' || r.code === 'rythme_different') return t(`offres.rythmes.${r.valeur}`);
  // Valeur française du serveur : citée (intitulé) ou isolée de gauche à droite dans une phrase arabe.
  if (typeof r.valeur !== 'string') return r.valeur;
  return r.code === 'metier' || r.code === 'metier_partiel' ? citation(r.valeur) : isolerLtr(r.valeur);
}

// La citation de l'intitulé vient du serveur : une fonction plus ancienne ne l'envoie pas.
const SANS_CITATION: Partial<Record<Raison['code'], string>> = {
  metier: 'offres.raisons.metier_sans_citation',
  metier_partiel: 'offres.raisons.metier_partiel_sans_citation',
};

export function libelleRaison(r: Raison): string {
  const repli = r.valeur === undefined ? SANS_CITATION[r.code] : undefined;
  return repli ? t(repli) : t(`offres.raisons.${r.code}`, { valeur: valeurAffichee(r) });
}

/**
 * « 24 mois », lu dans le libellé du contrat (« CDD - 24 Mois », « Apprentissage · 24 mois ») ou les
 * détails, puis écrit dans la langue de l'interface (pluriel : l'arabe change de forme de 1 à 2, de 3 à 10
 * et au-delà).
 */
export function dureeContrat(offre: Offre): string | null {
  const sources = [offre.contrat.libelle, ...offre.details.filter((d) => d.cle === 'duree_contrat' || d.cle === 'type_contrat').map((d) => d.valeur)];
  for (const texte of sources) {
    const m = /(\d+)\s*mois/i.exec(texte ?? '');
    if (m) return t('offres.carte.dureeMois', { count: Number(m[1]) });
  }
  return null;
}

/** Le salaire annoncé, seulement s'il porte un montant : « Selon profil » n'en est pas un. */
export function salaireAffiche(offre: Offre): string | null {
  const s = offre.details.find((d) => d.cle === 'salaire')?.valeur ?? null;
  return s && /\d/.test(s) ? s : null;
}

/** Au-delà, l'intitulé passe en police réduite pour tenir en quatre lignes sans être coupé. */
export const TITRE_LONG = 60;
export const titreLong = (titre: string) => titre.length > TITRE_LONG;

/** Licence France Travail (art. 4) : la source est toujours nommée. */
export function sourceLibelle(source: SourceOffre, partenaire: string | null): string {
  if (source === 'france_travail') return t('offres.sources.france_travail');
  if (source === 'adzuna') return t('offres.sources.adzuna');
  return partenaire ? `${t('offres.sources.lba')} · ${partenaire}` : t('offres.sources.lba');
}

export function initiales(nom: string | null): string {
  const mots = (nom ?? '').trim().split(/\s+/).filter(Boolean);
  if (mots.length === 0) return '?';
  if (mots.length === 1) return mots[0].slice(0, 2).toUpperCase();
  return (mots[0][0] + mots[1][0]).toUpperCase();
}

export function joursDepuis(date: string | null, maintenant: Date = new Date()): number | null {
  if (!date) return null;
  const d = new Date(date).getTime();
  if (Number.isNaN(d)) return null;
  return Math.max(0, Math.floor((maintenant.getTime() - d) / 86_400_000));
}

/** `en-GB` et non `en-US` : l'ordre jour-mois-année reste celui des offres françaises qu'il date. */
export function dateCourte(date: string | null): string | null {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(localeFormats(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}
